import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { SbLocalDateTime } from '@ngx-seatback/api-helper';
import { TranslateService } from '@ngx-translate/core';
import { Duration } from 'luxon';
import { FilterListComponent } from 'src/app/pages/datasets/filter-list/filter-list.component';
import {
  API,
  ContentShareFullDTO,
  CullDTO,
  TimeCullDTO,
  GeoCullDTO,
  PlayerCullDTO,
  PlanningDTO,
  DistributionBasis,
} from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { Observable } from 'rxjs';

type CullType = 'PlayerCull' | 'GeoCull' | 'TimeCull';

@Component({
  selector: 'sb-page-content-share-edit',
  templateUrl: './content-share-edit.component.html',
  styleUrls: ['./content-share-edit.component.scss'],
  standalone: false,
})
export class ContentShareEditComponent implements OnInit {
  @Input({ required: true }) contentShare: ContentShareFullDTO;
  @Input() planning: PlanningDTO;
  @Output() contentShareChanged: EventEmitter<ContentShareFullDTO>;

  loading: boolean;
  timeCull: TimeCullDTO;
  geoCull: GeoCullDTO;
  playerCull: PlayerCullDTO;
  distributionBasis: Array<keyof typeof DistributionBasis> = Object.keys(DistributionBasis).filter(key => isNaN(Number(key))) as Array<
    keyof typeof DistributionBasis
  >;

  form: UntypedFormGroup;
  disableSubmit: boolean;

  get Name() {
    return this.form.get('Name');
  }

  get Description() {
    return this.form.get('Description');
  }
  get From() {
    return this.form.get('From');
  }
  get Till() {
    return this.form.get('Till');
  }

  constructor(
    private readonly api: API,
    public readonly modal: ModalService,
    private readonly errorService: ErrorService,
    public readonly translate: TranslateService
  ) {
    this.disableSubmit = false;
    this.form = new UntypedFormGroup({
      Name: new UntypedFormControl('', [Validators.required]),
      Description: new UntypedFormControl('', []),
      From: new UntypedFormControl('', []),
      Till: new UntypedFormControl('', []),
    });
    this.loading = true;
    this.contentShareChanged = new EventEmitter<ContentShareFullDTO>();
  }

  ngOnInit(): void {
    this.registerFormChangesListener();
    if (this.contentShare) {
      this.updateForm();
    } else {
      this.contentShare = new ContentShareFullDTO();
      this.contentShare.quotaDistributionBasis = (this.distributionBasis[0] as DistributionBasis) ?? DistributionBasis.Count;
      this.contentShare.quotas = [];
    }
    this.loading = false;
  }

  private registerFormChangesListener() {
    this.Name.valueChanges.subscribe(value => {
      this.contentShare.name = value;
    });
    this.Description.valueChanges.subscribe(value => {
      this.contentShare.description = value;
    });
    this.From.valueChanges.subscribe(value => {
      let diff: Duration = null;
      if (this.contentShare?.validTill?.value && this.contentShare?.validFrom?.value) {
        diff = this.contentShare.validTill.value.diff(this.contentShare.validFrom.value);
      }
      this.contentShare.validFrom = value;
      if (this.contentShare?.validFrom?.value && this.contentShare?.validTill?.value) {
        this.contentShare.validTill = diff
          ? new SbLocalDateTime(this.contentShare.validFrom.value.plus(diff))
          : this.contentShare.validFrom;
        setTimeout(() => {
          this.Till.setValue(this.contentShare.validTill);
        }, 1);
      }
    });
    this.Till.valueChanges.subscribe(value => {
      this.contentShare.validTill = value;
    });
  }

  updateForm() {
    this.Name.setValue(this.contentShare.name);
    this.Name.markAsTouched();
    this.Description.setValue(this.contentShare.description);
    this.Description.markAsTouched();
    this.From.setValue(this.contentShare.validFrom);
    this.Till.setValue(this.contentShare.validTill);
  }

  updateContentShare() {
    this.disableSubmit = true;
    let obs: Observable<ContentShareFullDTO>;
    if (this.planning) {
      if (this.contentShare.id) {
        if (!this.contentShare.planning) {
          obs = this.api.ds_cm_ContentShare_createContentShareOverwriteInPlanning(
            this.planning.id,
            this.contentShare.id,
            this.contentShare
          );
        } else {
          obs = this.api.ds_cm_ContentShare_editContentShareInPlanning(this.planning.id, this.contentShare);
        }
      } else {
        obs = this.api.ds_cm_ContentShare_createContentShareInPlanning(this.planning.id, this.contentShare);
      }
    } else if (this.contentShare.id) {
      obs = this.api.ds_cm_ContentShare_editContentShare(this.contentShare);
    } else {
      obs = this.api.ds_cm_ContentShare_createContentShare(this.contentShare);
    }
    obs.subscribe({
      next: (data: ContentShareFullDTO) => {
        this.contentShareChanged.emit(data);
        this.disableSubmit = false;
      },
      error: error => {
        this.errorService.errorHandler(error);
        this.disableSubmit = false;
      },
    });
  }

  localDateTimeInputFrom(date: SbLocalDateTime) {
    this.From.setValue(date);
  }

  localDateTimeInputTill(date: SbLocalDateTime) {
    this.Till.setValue(date);
  }

  selectFilter(type: CullType) {
    this.modal
      .showNavigationModal({
        entryComponent: { value: FilterListComponent, data: { preselectType: type } },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((result: CullDTO) => {
        if (result) {
          switch (type) {
            case 'TimeCull': {
              this.contentShare.timeCull = result as TimeCullDTO;
              break;
            }
            case 'GeoCull': {
              this.contentShare.geoCull = result as GeoCullDTO;
              break;
            }
            case 'PlayerCull': {
              this.contentShare.playerCull = result as PlayerCullDTO;
              break;
            }
          }
        }
      })
      .catch(() => {});
  }

  checkPercentage() {
    if (!this.contentShare.quotas || this.contentShare.quotas.length === 0) {
      return true;
    }
    return this.contentShare.quotas.reduce((percentage, quota) => percentage + quota.share, 0) !== 100;
  }
}
